  import React, {
    useEffect,
    useRef,
    useState,
    useMemo,
    useCallback,
  } from "react";
  import { useParams, useNavigate } from "react-router-dom";
  import DOMPurify from "dompurify";
  import { toast } from "sonner";

  import {
    useGetCourseBySlugQuery,
    useGetCourseProgressQuery,
    useStartLessonExerciseMutation,
    useSaveExerciseAnswerMutation,
    useSubmitLessonExerciseMutation,
    useUpdateLessonCompletionMutation,
    useSaveCourseResultMutation,
  } from "@/features/api/courseApi";
  import { useSearchParams } from "react-router-dom";

  import { Button } from "@/components/ui/button";
  import { CardFooter } from "@/components/ui/card";
  import { Skeleton } from "@/components/ui/skeleton";
  import { Progress } from "@/components/ui/progress";

  import {
    ArrowRight,
    PlayCircle,
    RotateCcw,
    ArrowLeft,
    ChevronDown,
  } from "lucide-react";

  /* ================= EXERCISES ================= */
  import MultipleChoiceExercise from "@/assets/AllCourses/Exercises/MultipleChoice";
  import FillInTheBlanksExercise from "@/assets/AllCourses/Exercises/FillInTheBlanks";
  import PictureFillInTheBlanksExercise from "@/assets/AllCourses/Exercises/PictureFillInTheBlanks";
  import MatchingExercise from "@/assets/AllCourses/Exercises/Matching";
  import PronunciationExercise from "@/assets/AllCourses/Exercises/Pronunciation";
  import ListeningExercise from "@/assets/AllCourses/Exercises/Listening";
  import DragAndDropExercise from "@/assets/AllCourses/Exercises/DragAndDrop";
  import CategorizingWordsExercise from "@/assets/AllCourses/Exercises/CategorizingWords";
  import CircleTheCorrectWordExercise from "@/assets/AllCourses/Exercises/CircleTheCorrectWord";
  import CompleteTheConversationExercise from "@/assets/AllCourses/Exercises/CompleteTheConversation";
  import CompleteTheSentencesExercise from "@/assets/AllCourses/Exercises/CompleteTheSentences";
  import CrosswordExercise from "@/assets/AllCourses/Exercises/Crossword";
  import MissingAlphabetsExercise from "@/assets/AllCourses/Exercises/MissingAlphabets";
  import SentenceJumblesExercise from "@/assets/AllCourses/Exercises/SentenceJumbles";
  import StressingSyllableWordsExercise from "@/assets/AllCourses/Exercises/StressingSyllableWords";
  import VocabularyBuildingExercise from "@/assets/AllCourses/Exercises/VocabularyBuilding";
  import WordFormationExercise from "@/assets/AllCourses/Exercises/WordFormation";
  import WordJumblesExercise from "@/assets/AllCourses/Exercises/WordJumbles";



  /* ================= CONSTANTS ================= */
  const SCROLL_OFFSET = 140;
  const PASS_PERCENT = 30;

  /* ================= HELPERS ================= */
  const exerciseComponents = {
    "multiple-choice": MultipleChoiceExercise,
    "fill-in-the-blanks": FillInTheBlanksExercise,
    "picture-fill-in-the-blanks": PictureFillInTheBlanksExercise,
    matching: MatchingExercise,
    pronunciation: PronunciationExercise,
    listening: ListeningExercise,
    "drag-and-drop": DragAndDropExercise,
    "categorizing-words": CategorizingWordsExercise,
    "circle-the-correct-word": CircleTheCorrectWordExercise,
    "complete-the-conversation": CompleteTheConversationExercise,
    "complete-the-sentences": CompleteTheSentencesExercise,
    crossword: CrosswordExercise,
    "missing-alphabets": MissingAlphabetsExercise,
    "sentence-jumbles": SentenceJumblesExercise,
    "stressing-syllable-words": StressingSyllableWordsExercise,
    "vocabulary-building": VocabularyBuildingExercise,
    "word-formation": WordFormationExercise,
    "word-jumbles": WordJumblesExercise,
    "exercise-section": () => null,
  };



  const getTitle = (ex, i) => ex?.title?.trim() || `Exercise ${i + 1}`;

  /* ================= COMPONENT ================= */
  export default function CourseLearningPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const startTimeRef = useRef(Date.now());
    const exerciseRefs = useRef([]);
    const lastSavedAnswersRef = useRef({});
    const [activeIndex, setActiveIndex] = useState(0);
    const [openAccordion, setOpenAccordion] = useState(true);

    const [answers, setAnswers] = useState({});
    const [attemptId, setAttemptId] = useState(null);

    const [showResult, setShowResult] = useState(false);
    const [retryMode, setRetryMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data, isLoading, refetch } = useGetCourseBySlugQuery(slug);
    const [updateLessonCompletion] = useUpdateLessonCompletionMutation();
    const [startAttempt] = useStartLessonExerciseMutation();
    const [saveAnswer] = useSaveExerciseAnswerMutation();
    const [submitAttempt] = useSubmitLessonExerciseMutation();
    const [saveCourseResult] = useSaveCourseResultMutation();

  const [searchParams] = useSearchParams();
  const lessonIdFromUrl = searchParams.get("lesson");

  const course = data?.course;
  const { data: progressData, isLoading: progressLoading } = useGetCourseProgressQuery(course?._id, {
    skip: !course?._id,
  });

  const lesson = useMemo(() => {
    if (!course?.lessons?.length) return null;

    return (
      course.lessons.find((l) => l._id === lessonIdFromUrl) ||
      course.lessons.find(
        (l) => l._id === progressData?.currentLessonId
      ) ||
      course.lessons[0]
    );
  }, [course, lessonIdFromUrl, progressData]);

  const exercises = lesson?.exercises || [];
  const realExercises = exercises.filter((e) => e.type !== "exercise-section");

  const progressValue =
    realExercises.length === 0
      ? 0
      : Math.round((Object.keys(answers).length / realExercises.length) * 100);

  const retryLesson = async () => {
    if (!lesson?._id) return;
    try {
      setAnswers({});
      setShowResult(false);
      setRetryMode(false);

      const res = await startAttempt({ lessonId: lesson._id }).unwrap();
      setAttemptId(res?.attemptId ?? null);

      startTimeRef.current = Date.now();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error("Retry failed");
    }
  };


  const continueLesson = async () => {
    try {
      if (!course || !lesson) return;

      await updateLessonCompletion({
        courseId: course._id,
        lessonId: lesson._id,
        completed: true,
      });

      const lessons = course?.lessons || [];

      const currentIndex = lessons.findIndex(
        (l) => l._id === lesson._id
      );

      const nextLesson = lessons[currentIndex + 1];

      // reset UI
      setAnswers({});
      setShowResult(false);
      setRetryMode(false);
      setAttemptId(null);

      window.scrollTo({ top: 0, behavior: "smooth" });

      // refresh backend data
      await refetch();

      if (nextLesson) {
        navigate(`/courses/${slug}?lesson=${nextLesson._id}`);
      } else {
        if (progressData?.isCompleted) {
          navigate(`/courses/${slug}/result`);
        } else {
          navigate(`/courses/${slug}`);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to continue");
    }
  };

    /* ================= START ATTEMPT ================= */
  useEffect(() => {
    if (!lesson?._id) return;

    const init = async () => {
      try {
        const res = await startAttempt({
          lessonId: lesson._id,
        }).unwrap();

        setAttemptId(res?.attemptId);

        startTimeRef.current = Date.now();

      } catch (e) {
        console.error(e);
      }
    };

    init();
  }, [lesson?._id]);

    /* ================= GROUP FOR TOC ================= */
    const grouped = useMemo(() => {
  if (!Array.isArray(exercises)) return [];

  const groups = [];
  let current = { title: "Exercises", items: [] };

  exercises.forEach((ex, i) => {
    if (ex.type === "exercise-section") {
      if (current.items.length) groups.push(current);
      current = { title: ex.title || "Section", items: [] };
    } else {
      current.items.push({ ...ex, index: i });
    }
  });

  if (current.items.length) groups.push(current);

  return groups;
}, [exercises]);

  /* ================= SCROLL SPY ================= */
    useEffect(() => {
    if (!exercises.length) return;

    const handleScroll = () => {
      let current = 0;

      for (let i = 0; i < exerciseRefs.current.length; i++) {
        const el = exerciseRefs.current[i];
        if (!el) continue;

        const rect = el.getBoundingClientRect();

        if (rect.top - SCROLL_OFFSET <= 0) {
          current = i;
        }
      }

      setActiveIndex((prev) => (prev !== current ? current : prev));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // run once initially
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [exercises]);

    /* ================= SCROLL FUNCTION ================= */
    const scrollToSection = useCallback((index) => {
      const el = exerciseRefs.current[index];
      if (!el) return;

      const y =
        el.getBoundingClientRect().top +
        window.scrollY -
        SCROLL_OFFSET;

      window.scrollTo({ top: y, behavior: "smooth" });
    }, []);


    /* ================= AUTO SAVE ================= */
    useEffect(() => {
      if (!attemptId || showResult) return;

      const interval = setInterval(async () => {
        try {
          const changed = Object.entries(answers).filter(([id, val]) => {
            const prev = lastSavedAnswersRef.current[id];
            return !prev || prev.answer !== val.answer;
          });

          if (!changed.length) return;

          await Promise.all(
            changed.map(([id, val]) =>
              saveAnswer({
                attemptId,
                exerciseId: id,
                answer: val.answer,
                correct: val.correct,
              })
            )
          );

          lastSavedAnswersRef.current = { ...answers };
        } catch (e) {
          console.error(e);
        }
      }, 2500);

      return () => clearInterval(interval);
    }, [answers, attemptId, showResult]);

    /* ================= ANSWER ================= */
    const handleComplete = useCallback((id, answer, correct) => {
      setAnswers((prev) => ({
        ...prev,
        [id]: { answer, correct },
      }));
    }, []);

    /* ================= SUBMIT ================= */
  const submit = async () => {
    if (isSubmitting) return;

    const real = exercises.filter(e => e.type !== "exercise-section");

    const payload = real
      .map(ex => {
        const a = answers[ex._id];
        if (!a) return null;

        return {
          exerciseId: ex._id,
          answer: a.answer,
          correct: a.correct,
        };
      })
      .filter(Boolean);

    if (payload.length !== real.length) {
      return toast.error("Complete all questions");
    }

    try {
      setIsSubmitting(true);

      const timeSpent = startTimeRef.current
        ? Math.floor((Date.now() - startTimeRef.current) / 1000)
        : 0;

      const res = await submitAttempt({
        attemptId,
        answers: payload,
        timeSpent,
      }).unwrap();

      await saveCourseResult({
        courseId: course._id,
        lessonId: lesson._id,
        score: res?.scorePercent || 0,
      });

      setShowResult(true);
      setRetryMode(res?.scorePercent < PASS_PERCENT);

    } catch (e) {
      console.error(e);
      toast.error(e?.data?.message || "Submit failed");
    } finally {
      setIsSubmitting(false);
    }
  };

    /* ================= UI ================= */
    if (isLoading || progressLoading) return <Skeleton className="h-40 w-full" />;

    if (!course) return <div className="p-8 text-red-600">Course not found</div>;

    return (
      <div className="flex gap-6 p-6">

        {/* ================= TOC ================= */}
        <aside className="w-80 bg-white p-6 rounded-xl shadow sticky top-6 h-fit">

          <button
            onClick={() => setOpenAccordion((p) => !p)}
            className="flex justify-between w-full font-bold text-xl mb-4"
          >
            {course.title}
            <ChevronDown className={openAccordion ? "rotate-180" : ""} />
          </button>

        <Progress value={progressValue} className="mb-4" />

          {openAccordion && grouped?.length > 0 &&
              grouped.map((group, gi) => (
              <div key={gi} className="mb-4">
                <h4 className="text-sm font-bold mb-2 pr-2">
                  {group.title}
                </h4>

                {group.items.map((ex) => {
            const active = activeIndex === ex.index;

                  return (
                    <button
                      key={ex.index}
                      onClick={() => scrollToSection(ex.index)}
                      className={`w-full text-left px-4 py-2 rounded mb-1
                      ${
                        active
                          ? "bg-blue-100 text-blue-700 border-l-4 border-blue-600"
                          : "hover:bg-gray-100 text-gray-600"
                      }`}
                    >
                      {getTitle(ex, ex.index)}
                    </button>
                  );
                })}
              </div>
            ))}
        </aside>

        {/* ================= MAIN ================= */}
        <main className="flex-1">
          {exercises.map((ex, i) => {
            const Comp = exerciseComponents[ex.type];
            if (!Comp) return null;

            const answerState = answers[ex._id];
            const isCorrect = answerState?.correct;
            const isAnswered = answerState !== undefined;

            return (
              <section
                key={i}
                data-index={i}
                ref={(el) => (exerciseRefs.current[i] = el)}
                className="mb-14 scroll-mt-[140px]"
              >
                <h3 className="text-xl font-bold mb-6">
                  {getTitle(ex, i)}
                </h3>

                {ex.details && (
                  <div
                    className="mb-6"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(ex.details),
                    }}
                  />
                )}

                {ex.type !== "exercise-section" && (
                <div className="p-6 rounded border bg-white">

                      <div
                        className={`rounded p-4 transition ${
                          showResult && isAnswered
                            ? isCorrect
                              ? "bg-green-50 border border-green-400"
                              : "bg-red-50 border border-red-400"
                            : ""
                        }`}
                      >
                    <Comp
                      exercise={ex}
                      showResult={showResult}
                      onComplete={(a, c) =>
                        handleComplete(ex._id, a, c)
                      }
                    />
                  </div>

                </div>
                )}
              </section>
            );
          })}

          <CardFooter className="flex justify-between mt-6">
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft /> Back
            </Button>

            {!showResult ? (
              <Button onClick={submit} disabled={isSubmitting}>
                Submit <ArrowRight />
              </Button>
            ) : retryMode ? (
              <Button variant="destructive" onClick={retryLesson}>
              <RotateCcw /> Retry
            </Button>
            ) : (
            <Button onClick={continueLesson}>
              Continue
            </Button>
            )}
          </CardFooter>
        </main>
      </div>
    );
  }